import { Usage } from "../flatbuffers/generated/usage/usage";
import {
  UsageMap,
  DOOR_USAGE_CODES,
  DIRECTION_USAGE_CODES,
  ELEVATOR_IDLE_USAGE_CODES,
  ElevatorIcon,
} from "../types/monitoringTypes";
import { SocketMessageInterface } from "../interfaces/Monitoring.interface";

// ---------------------------------------------------------------------------
// Door configuration helpers
// ---------------------------------------------------------------------------

/**
 * Returns true when the floors have both front and rear doors configured,
 * meaning the UI should show double door icons (e.g. elFrontCloseRearOpen)
 * even for floors that only have one door type.
 *
 * `frontDoor`/`rearDoor` are expected to already be normalized (see
 * MonitoringBank.tsx's normalizeFloors/normalizeLiftFloors) before reaching
 * here, which means an un-hydrated floor reads as "has a front door" — so a
 * lift with real rear-door data but no hydrated front-door data anywhere
 * will report as mixed. That follows the same door-presence convention this
 * field uses everywhere else in monitoring; it is not a sign of a genuinely
 * mixed configuration on its own.
 */
export function hasMixedDoorConfig(
  floors:
    | ReadonlyArray<{
        frontDoor?: boolean;
        rearDoor?: boolean;
      }>
    | undefined,
): boolean {
  if (!floors || floors.length === 0) return false;
  const hasFront = floors.some((f) => f.frontDoor);
  const hasRear = floors.some((f) => f.rearDoor);
  return hasFront && hasRear;
}

// ---------------------------------------------------------------------------
// Typed usage-map helpers
// ---------------------------------------------------------------------------

/** The four usage codes each side reports, kept beside the sides they belong to. */
const FRONT_DOOR_USAGE_CODES: readonly Usage[] = [
  Usage.FrontDoorOpened,
  Usage.FrontDoorClosed,
  Usage.FrontDoorOpening,
  Usage.FrontDoorClosing,
];

const REAR_DOOR_USAGE_CODES: readonly Usage[] = [
  Usage.RearDoorOpened,
  Usage.RearDoorClosed,
  Usage.RearDoorOpening,
  Usage.RearDoorClosing,
];

/**
 * Whether a usage map carries any door information at all.
 *
 * `sides` narrows the question to the doors a bank actually has. Without it the
 * answer is about all eight codes, which is wrong wherever the caller has
 * already MASKED the unconfigured side out of its icon computation: an
 * unconfigured door would report "yes, there is door data", contribute no icon
 * because the mask discarded it, and leave the caller to conclude that
 * something is wrong. Omitting `sides` keeps the original whole-map behaviour
 * for callers that do no masking.
 */
export function usageHasDoorData(
  usage: UsageMap,
  sides?: { hasFrontDoor: boolean; hasRearDoor: boolean },
): boolean {
  if (!sides) return DOOR_USAGE_CODES.some((code) => code in usage);

  const codes = [
    ...(sides.hasFrontDoor ? FRONT_DOOR_USAGE_CODES : []),
    ...(sides.hasRearDoor ? REAR_DOOR_USAGE_CODES : []),
  ];

  return codes.some((code) => code in usage);
}

/**
 * Whether a usage map carries direction information at all.
 *
 * Idle codes (Parked, NormalStopped, FaultedStopped) count: "not travelling" is
 * a direction report, and applyDeltaToState already resolves them to a null
 * direction rather than ignoring them. As with the travel codes, presence is
 * what counts, not the asserted value.
 */
export function usageHasDirectionData(usage: UsageMap): boolean {
  return (
    DIRECTION_USAGE_CODES.some((code) => code in usage) ||
    ELEVATOR_IDLE_USAGE_CODES.some((code) => code in usage)
  );
}

// ---------------------------------------------------------------------------
// resolveSteadyDoorState (adopted from alejandroTamayoSES)
// ---------------------------------------------------------------------------

/**
 * Resolve a single door's steady-state (open/closed) from whichever signals
 * the adapter actually reports.
 *
 * Some adapters (e.g. LA Metro Rail) only ever send ONE side of the pair —
 * `FrontDoorOpened` toggling true/false — and never send `FrontDoorClosed`
 * at all, in any message, ever. For those adapters "Opened=false" is not
 * ambiguous: it unambiguously means "not open", i.e. closed. Treating that
 * as an ambiguous transition (as we used to, via `code in usage`) made the
 * rendered icon depend on whether the *other* signal happened to have been
 * observed yet in the current WebSocket session — which is exactly the
 * "depends on incidental timing between messages" behavior we want to avoid.
 * A reconnect, a page reload, or simply catching the stream mid-cycle could
 * each produce a different answer for identical adapter behavior.
 *
 * Only when the adapter demonstrably reports BOTH signals independently do
 * we treat Open=false/Closed=false as a genuine ambiguous state (the caller
 * renders it as Warning rather than guessing Open/Closed/Opening); that case
 * is a real property of a two-signal door, not an artifact of session history.
 */
export function resolveSteadyDoorState(
  usage: UsageMap,
  openedCode: Usage,
  closedCode: Usage,
): { open: boolean; closed: boolean; hasBothSignals: boolean } {
  const hasOpened = openedCode in usage;
  const hasClosed = closedCode in usage;
  const openedValue = usage[openedCode] ?? false;
  const closedValue = usage[closedCode] ?? false;

  if (hasOpened && !hasClosed) {
    return { open: openedValue, closed: !openedValue, hasBothSignals: false };
  }
  if (!hasOpened && hasClosed) {
    // Single-signal Closed: treat as deterministically closed regardless of
    // the signal value.  Both Closed=true (door is closed) and Closed=false
    // (adapter misreporting / unreliable signal) should resolve to closed.
    return { open: false, closed: true, hasBothSignals: false };
  }
  return { open: openedValue, closed: closedValue, hasBothSignals: hasOpened && hasClosed };
}

// ---------------------------------------------------------------------------
// Door state model (Improvement #1 + #2)
// ---------------------------------------------------------------------------

/**
 * The resolved state of a single door side (front or rear).
 *
 * - `None`: no data for this door at all (no keys in the adapter's message)
 * - `Resolved`: we know the door's state (transition active, or open/closed is true)
 * - `Unknown`: we have a complete Opened+Closed pair (both false) and no
 *   transition capability — the adapter has no way to tell us "opening" or
 *   "closing". We render this as Warning (we should know but don't).
 * - `Unresolved`: incomplete pair, or the adapter has transition capability
 *   but isn't using it — we should know the state but can't determine it.
 * - `Conflict`: Opened and Closed are BOTH true. On a discrete-IO adapter each
 *   comes from its own limit switch on its own pin, so the two can physically
 *   assert together — a stuck switch, a miswired input, a mis-set polarity. It
 *   is not ambiguity like `Unknown`; the hardware is stating two things that
 *   cannot both hold, and the operator needs to see that rather than a plausible
 *   door icon picked from one half of a contradiction.
 *
 * This model replaces the previous boolean-tangle derivation logic with a
 * single-source-of-truth state that is easy to reason about, test, and extend.
 */
export enum DoorState {
  None = "none",
  Resolved = "resolved",
  Unknown = "unknown",
  Unresolved = "unresolved",
  Conflict = "conflict",
}

/**
 * Configuration for door icon resolution.
 *
 * Improvement #3: replaces the ambiguous boolean `doorUnknownAsOpening` flag
 * with a typed object that makes intent explicit at every call site.
 */
export interface DoorIconConfig {
  /**
   * When the Open/Closed pair is complete but reads both false, does that mean
   * the door is MOVING, or that something is wrong?
   *
   * A two-signal door sits in that state for the whole interval between its two
   * limit switches, so on a site whose adapter never sends Opening/Closing
   * codes it is simply what travel looks like — and rendering a warning there
   * puts an alarm on the most ordinary event a lift produces. On a site that
   * does report transitions, the same reading is a genuine anomaly.
   *
   * The difference is a property of the installation, not of this module, which
   * is why it is an operator setting rather than an inference. `true` renders
   * the transition icon; `false` renders the warning.
   *
   * NOT deprecated, despite what this comment used to claim.
   * `usageToDoorIconFiltered` accepted the value and never read it, so the flag
   * appeared dead and was documented as dead — while every caller was faithfully
   * passing it in. Honouring it again is the fix, not a revival of a bad idea.
   */
  deriveUnknownAsOpening?: boolean;
}

/**
 * Resolve a single door's state from the usage map.
 *
 * Improvement #1: extracted from inline boolean logic into a named function
 * with a clear state model. This eliminates the 20-line duplication that
 * previously existed between `usageToDoorIcon` and `usageToDoorIconFiltered`.
 *
 * Uses `resolveSteadyDoorState` to handle the single-signal case (e.g. LA
 * Metro Rail sends only FrontDoorOpened, never FrontDoorClosed).
 */
export function resolveDoorState(
  usage: UsageMap,
  hasDoor: boolean,
  side: "front" | "rear",
  config: DoorIconConfig = {},
): DoorState {
  // Coerce boolean → DoorIconConfig for backward compat with callers that pass
  // a bare boolean instead of a DoorIconConfig object.
  const resolvedConfig: Required<DoorIconConfig> =
    typeof config === "boolean"
      ? { deriveUnknownAsOpening: config }
      : { deriveUnknownAsOpening: config.deriveUnknownAsOpening ?? false, ...config };
  if (!hasDoor) return DoorState.None;

  const openKey = side === "front" ? Usage.FrontDoorOpened : Usage.RearDoorOpened;
  const closedKey = side === "front" ? Usage.FrontDoorClosed : Usage.RearDoorClosed;
  const openingKey = side === "front" ? Usage.FrontDoorOpening : Usage.RearDoorOpening;
  const closingKey = side === "front" ? Usage.FrontDoorClosing : Usage.RearDoorClosing;

  const hasOpened = openKey in usage;
  const hasClosed = closedKey in usage;
  const hasTransition = openingKey in usage || closingKey in usage;

  // No signals at all → Unresolved
  if (!hasOpened && !hasClosed && !hasTransition) return DoorState.Unresolved;

  // Only transition signals (no Opened/Closed pair) → Unresolved
  if (!hasOpened && !hasClosed && hasTransition) return DoorState.Unresolved;

  // Only one side of the pair exists (single-signal adapter)
  if (hasOpened && !hasClosed) {
    // Single-signal: Opened value determines state
    return DoorState.Resolved;
  }
  if (!hasOpened && hasClosed) {
    // Single-signal: Closed value determines state
    return DoorState.Resolved;
  }

  // Both signals exist (complete pair)
  const openedValue = usage[openKey] ?? false;
  const closedValue = usage[closedKey] ?? false;

  // Both false with transition → Unresolved (adapter has transition capability
  // but both steady states are false — genuine ambiguity)
  if (!openedValue && !closedValue && hasTransition) {
    return DoorState.Unresolved;
  }

  // Both false with no transition → ambiguous (Unknown if deriving)
  if (!openedValue && !closedValue && !hasTransition) {
    if (resolvedConfig.deriveUnknownAsOpening) return DoorState.Unknown;
    return DoorState.Resolved;
  }

  // Both true → the door cannot be open and closed at once. Checked before
  // the Resolved catch-all, which would otherwise accept the pair on the
  // strength of "at least one is true" and render a door icon derived from
  // half a contradiction.
  if (openedValue && closedValue) {
    return DoorState.Conflict;
  }

  // Pair exists with at least one true or transition → Resolved
  return DoorState.Resolved;
}

// ---------------------------------------------------------------------------
// Icon composition (Improvement #4)
// ---------------------------------------------------------------------------

/**
 * Lookup table for composing per-door icons from resolved states.
 *
 * Format: `${frontState}|${rearState}` → ElevatorIcon.
 * Patterns starting with `*` match any state on that side.
 *
 * We never derive elOpening — an Opening icon is only ever rendered when the
 * adapter has actually sent an explicit Opening/Closing usage code (handled
 * by resolveDoorIcon). The Unknown state renders as Warning: "we should know
 * but don't."
 */
export const DERIVED_ICON_MAP: Partial<Record<string, ElevatorIcon>> = {
  // Conflict on either side wins over whatever the other side reports: a door
  // insisting it is both open and closed is the thing the operator needs to
  // see, and no combination makes it less true.
  "conflict|*": ElevatorIcon.Warning,
  "*|conflict": ElevatorIcon.Warning,
  // Unknown combinations
  "unknown|unknown": ElevatorIcon.Warning,
  "unknown|resolved": ElevatorIcon.Warning,
  "resolved|unknown": ElevatorIcon.Warning,
  "unknown|unresolved": ElevatorIcon.Warning,
  "unresolved|unknown": ElevatorIcon.Warning,
  "none|unknown": ElevatorIcon.Warning,
  "unknown|none": ElevatorIcon.Warning,
  // Resolved combinations
  "resolved|resolved": ElevatorIcon.FrontCloseRearClose,
  "resolved|none": ElevatorIcon.FrontCloseRearClose,
  "none|resolved": ElevatorIcon.FrontCloseRearClose,
  "none|none": ElevatorIcon.FrontCloseRearClose,
  // Unresolved combinations
  "resolved|unresolved": ElevatorIcon.Warning,
  "unresolved|resolved": ElevatorIcon.Warning,
  "unresolved|unresolved": ElevatorIcon.Warning,
  "unresolved|none": ElevatorIcon.Warning,
  "none|unresolved": ElevatorIcon.Warning,
  // Wildcard fallbacks
  "*|unresolved": ElevatorIcon.Warning,
  "unresolved|*": ElevatorIcon.Warning,
};

/**
 * Compose a door icon from two resolved DoorStates.
 *
 * Improvement #4: uses a lookup table instead of if/else chains. Every
 * combination is explicit and verifiable. Adding a new DoorState variant
 * requires only updating the table — no conditional logic to touch.
 */
export function composeDoorIcon(front: DoorState, rear: DoorState): ElevatorIcon {
  // Check exact match first
  const exactKey = `${front}|${rear}`;
  if (exactKey in DERIVED_ICON_MAP) {
    return DERIVED_ICON_MAP[exactKey]!;
  }

  // Fall back to wildcard patterns
  const frontWildcard = `*|${rear}`;
  if (frontWildcard in DERIVED_ICON_MAP) {
    return DERIVED_ICON_MAP[frontWildcard]!;
  }

  const rearWildcard = `${front}|*`;
  if (rearWildcard in DERIVED_ICON_MAP) {
    return DERIVED_ICON_MAP[rearWildcard]!;
  }

  // Safety net — should never be reached with a complete table
  return ElevatorIcon.Warning;
}

// ---------------------------------------------------------------------------
// Core door-state resolution from a UsageMap (no prefix, no bitmask)
// ---------------------------------------------------------------------------

/**
 * Resolve door icon from a typed UsageMap (ignores direction).
 *
 * @see https://liftnet.atlassian.net/browse/LIFT-462
 */
function resolveDoorIcon(
  frontOpen: boolean,
  frontClosed: boolean,
  frontOpening: boolean,
  frontClosing: boolean,
  rearOpen: boolean,
  rearClosed: boolean,
  rearOpening: boolean,
  rearClosing: boolean,
): ElevatorIcon | null {
  const frontTransition = frontOpening || frontClosing;
  const rearTransition = rearOpening || rearClosing;

  // Transition states (opening/closing) take priority over steady states
  if (frontTransition && rearTransition) return ElevatorIcon.FrontOpeningRearOpening;
  if (frontTransition && rearOpen) return ElevatorIcon.FrontOpeningRearOpen;
  if (frontTransition && rearClosed) return ElevatorIcon.FrontOpeningRearClose;
  if (frontOpen && rearTransition) return ElevatorIcon.FrontOpenRearOpening;
  if (frontClosed && rearTransition) return ElevatorIcon.FrontCloseRearOpening;
  if (frontTransition) return ElevatorIcon.Opening;
  if (rearTransition) return ElevatorIcon.RearOpening;

  if (frontOpen && rearOpen) return ElevatorIcon.FrontOpenRearOpen;
  if (frontOpen && rearClosed) return ElevatorIcon.FrontOpenRearClose;
  if (frontClosed && rearOpen) return ElevatorIcon.FrontCloseRearOpen;
  if (frontClosed && rearClosed) return ElevatorIcon.FrontCloseRearClose;
  if (frontOpen) return ElevatorIcon.Open;
  if (frontClosed) return ElevatorIcon.Close;
  if (rearOpen) return ElevatorIcon.RearOpen;
  if (rearClosed) return ElevatorIcon.RearClose;

  return null;
}

/**
 * Resolve door icon for a mixed-door bank (has both front and rear doors across
 * its floors). Always returns double icons so the UI shows both door states even
 * when a particular floor has only one configured door type.
 *
 * - Floor with front door only → rear assumed closed
 * - Floor with rear door only → front assumed closed
 * - Floor with both → normal double icon resolution
 */
function resolveDoorIconMixed(
  hasFrontDoor: boolean,
  hasRearDoor: boolean,
  frontOpen: boolean,
  frontClosed: boolean,
  frontOpening: boolean,
  frontClosing: boolean,
  rearOpen: boolean,
  rearClosed: boolean,
  rearOpening: boolean,
  rearClosing: boolean,
): ElevatorIcon | null {
  const frontTransition = frontOpening || frontClosing;
  const rearTransition = rearOpening || rearClosing;

  if (hasFrontDoor && !hasRearDoor) {
    // Front door only — return single-door icons
    if (frontTransition) return ElevatorIcon.Opening;
    if (frontOpen) return ElevatorIcon.Open;
    if (frontClosed) return ElevatorIcon.Close;
    return null;
  }

  if (!hasFrontDoor && hasRearDoor) {
    // Rear door only — return single-door icons
    if (rearTransition) return ElevatorIcon.RearOpening;
    if (rearOpen) return ElevatorIcon.RearOpen;
    if (rearClosed) return ElevatorIcon.RearClose;
    return null;
  }

  // Both doors configured — always render double-door icons so a Child-Car-style
  // setup (front+rear on the same car) keeps both gold and blue visible even when
  // only one side has reported a state yet. Match the single-door branches above:
  // when one side has no data, assume that side is closed.
  if (frontTransition && rearTransition) return ElevatorIcon.FrontOpeningRearOpening;
  if (frontTransition && rearOpen) return ElevatorIcon.FrontOpeningRearOpen;
  if (frontTransition) return ElevatorIcon.FrontOpeningRearClose;
  if (frontOpen && rearTransition) return ElevatorIcon.FrontOpenRearOpening;
  if (frontClosed && rearTransition) return ElevatorIcon.FrontCloseRearOpening;
  if (rearTransition) return ElevatorIcon.FrontCloseRearOpening;

  if (frontOpen && rearOpen) return ElevatorIcon.FrontOpenRearOpen;
  if (frontOpen && rearClosed) return ElevatorIcon.FrontOpenRearClose;
  if (frontClosed && rearOpen) return ElevatorIcon.FrontCloseRearOpen;
  if (frontClosed && rearClosed) return ElevatorIcon.FrontCloseRearClose;
  if (frontOpen) return ElevatorIcon.FrontOpenRearClose;
  if (frontClosed) return ElevatorIcon.FrontCloseRearClose;
  if (rearOpen) return ElevatorIcon.FrontCloseRearOpen;
  if (rearClosed) return ElevatorIcon.FrontCloseRearClose;

  return null;
}

// ---------------------------------------------------------------------------
// Public API — accepts UsageMap directly
// ---------------------------------------------------------------------------

/**
 * Resolve an elevator icon from a typed UsageMap.
 * Direction (Up/Down) takes priority; when idle, falls through to door state.
 * Returns null when no door data has been received.
 */
export function usageToIcon(usage: UsageMap): string | null {
  const up = usage[Usage.Up] ?? false;
  const down = usage[Usage.Down] ?? false;

  if (up && !down) return ElevatorIcon.Up;
  if (down && !up) return ElevatorIcon.Down;

  const front = resolveSteadyDoorState(usage, Usage.FrontDoorOpened, Usage.FrontDoorClosed);
  const rear = resolveSteadyDoorState(usage, Usage.RearDoorOpened, Usage.RearDoorClosed);

  // Detect the both-false anomaly (complete Open/Closed pair, both false, no
  // transition capability) before calling resolveDoorIcon.
  const frontHasTransition = Usage.FrontDoorOpening in usage || Usage.FrontDoorClosing in usage;
  const rearHasTransition = Usage.RearDoorOpening in usage || Usage.RearDoorClosing in usage;

  const frontAnomaly = front.hasBothSignals && !front.open && !front.closed && !frontHasTransition;
  const rearAnomaly = rear.hasBothSignals && !rear.open && !rear.closed && !rearHasTransition;
  if (frontAnomaly || rearAnomaly) {
    return ElevatorIcon.Warning;
  }

  const icon = resolveDoorIcon(
    front.open,
    front.closed,
    usage[Usage.FrontDoorOpening] ?? false,
    usage[Usage.FrontDoorClosing] ?? false,
    rear.open,
    rear.closed,
    usage[Usage.RearDoorOpening] ?? false,
    usage[Usage.RearDoorClosing] ?? false,
  );
  if (icon) return icon;

  if (!usageHasDoorData(usage)) return null;
  return ElevatorIcon.Warning;
}

/**
 * Resolve door icon from a typed UsageMap (ignores direction).
 * Returns null when no door data has been received.
 *
 * We never guess elOpening: an Opening/Closing icon is only ever rendered
 * when the adapter has actually sent an explicit Opening/Closing usage code
 * (see resolveDoorIcon's transition inputs). When both Opened and Closed are
 * observed but currently both false with no Opening/Closing message, we
 * render elWarning — a genuine "we don't know" anomaly, not something to
 * animate as motion.
 *
 * @see https://liftnet.atlassian.net/browse/LIFT-462
 */
export function usageToDoorIcon(
  usage: UsageMap,
  // Accept boolean for backward compat: callers pass doorUnknownAsOpening (boolean)
  config?: DoorIconConfig | boolean,
): string | null {
  // Coerce boolean → DoorIconConfig so the call site type-checks cleanly.
  // deriveUnknownAsOpening is coerced to boolean to avoid undefined issues.
  const resolvedConfig: Required<DoorIconConfig> =
    typeof config === "boolean"
      ? { deriveUnknownAsOpening: config }
      : { deriveUnknownAsOpening: config?.deriveUnknownAsOpening ?? false, ...config };
  const front = resolveSteadyDoorState(usage, Usage.FrontDoorOpened, Usage.FrontDoorClosed);
  const rear = resolveSteadyDoorState(usage, Usage.RearDoorOpened, Usage.RearDoorClosed);

  // Detect the both-false anomaly (complete Open/Closed pair, both false, no
  // transition capability) before calling resolveDoorIcon, which cannot
  // distinguish "signal absent" from "signal false" and would return
  // FrontCloseRearClose instead of Warning.
  const frontHasTransition = Usage.FrontDoorOpening in usage || Usage.FrontDoorClosing in usage;
  const rearHasTransition = Usage.RearDoorOpening in usage || Usage.RearDoorClosing in usage;

  const frontBothFalse =
    front.hasBothSignals && !front.open && !front.closed && !frontHasTransition;
  const rearBothFalse = rear.hasBothSignals && !rear.open && !rear.closed && !rearHasTransition;

  // When both pairs are complete with both false (no transition capability),
  // it's a genuine anomaly regardless of deriveUnknownAsOpening.
  if (frontBothFalse && rearBothFalse) {
    return ElevatorIcon.Warning;
  }

  // When exactly one pair is complete with both false and deriveUnknownAsOpening
  // is true, treat that door as Opening (simulate transition).  Pre-process
  // the values so resolveDoorIcon gets the correct inputs (it can't
  // distinguish "signal absent" from "signal false").  When a door has no
  // data at all, treat it as closed so resolveDoorIcon produces the right
  // double-door icon.
  //
  // Note: deriveUnknownAsOpening only applies to the front door.  When the
  // rear pair is both-false, it's always treated as an anomaly (Warning),
  // regardless of deriveUnknownAsOpening.
  const frontOpen = frontBothFalse && resolvedConfig.deriveUnknownAsOpening ? false : front.open;
  const frontClosed =
    frontBothFalse && resolvedConfig.deriveUnknownAsOpening ? false : front.closed;
  const frontTransition =
    (frontHasTransition
      ? usage[Usage.FrontDoorOpening] || usage[Usage.FrontDoorClosing]
      : Boolean(frontBothFalse && resolvedConfig.deriveUnknownAsOpening)) ?? false;

  const rearOpen = rear.open;
  const rearClosed =
    !rear.hasBothSignals && !rearHasTransition && !rearBothFalse ? true : rear.closed;
  const rearTransition =
    (rearHasTransition ? usage[Usage.RearDoorOpening] || usage[Usage.RearDoorClosing] : false) ??
    false;

  // When one pair is both-false (no transition) and deriveUnknownAsOpening is
  // false, treat it as an anomaly → Warning.
  if (frontBothFalse && !resolvedConfig.deriveUnknownAsOpening) {
    return ElevatorIcon.Warning;
  }
  if (rearBothFalse && !resolvedConfig.deriveUnknownAsOpening) {
    return ElevatorIcon.Warning;
  }

  const icon = resolveDoorIcon(
    frontOpen,
    frontClosed,
    frontTransition,
    frontTransition,
    rearOpen,
    rearClosed,
    rearTransition,
    rearTransition,
  );
  if (icon) return icon;

  if (!usageHasDoorData(usage)) return null;

  // Both signals were observed for this door but currently read
  // Open=false/Closed=false with no Opening/Closing message ever received:
  // a genuine "we don't know" anomaly. Compose per-door icons via the lookup
  // table so each door's state is reflected (even if both resolve to Warning).
  // Note: frontHasTransition / rearHasTransition are already defined above.

  let frontState: DoorState;
  if (front.hasBothSignals) {
    frontState = front.open || front.closed ? DoorState.Resolved : DoorState.Unknown;
  } else if (frontHasTransition) {
    // Only transition signals, no Opened/Closed pair → Unresolved
    frontState = DoorState.Unresolved;
  } else {
    frontState = DoorState.Resolved;
  }
  let rearState: DoorState;
  if (rear.hasBothSignals) {
    rearState = rear.open || rear.closed ? DoorState.Resolved : DoorState.Unknown;
  } else if (rearHasTransition) {
    rearState = DoorState.Unresolved;
  } else {
    rearState = DoorState.Resolved;
  }
  return composeDoorIcon(frontState, rearState);
}

/**
 * Is this door's pair complete but reading neither open nor closed?
 *
 * A two-signal door sits like this for the whole interval between its two limit
 * switches — every single time it travels. Whether that means "moving" or
 * "something is wrong" is not something this module can know, which is exactly
 * why `doorUnknownAsOpening` exists for an operator to say.
 */
function pairCompleteButUnresolved(steady: {
  open: boolean;
  closed: boolean;
  hasBothSignals: boolean;
}): boolean {
  return steady.hasBothSignals && !steady.open && !steady.closed;
}

/**
 * Resolve door icon from a typed UsageMap, filtering out unconfigured doors.
 *
 * elOpening is never GUESSED from a config flag — that was the deprecated
 * `deriveUnknownAsOpening` behaviour and it stays gone. It is rendered either
 * when the adapter explicitly sent an Opening/Closing code, or when the door is
 * demonstrably in transit: see `inTransit` above, which infers motion only from
 * the adapter's own demonstrated vocabulary, never from configuration.
 *
 * @param bankHasMixedDoors - When true, the bank has both front and rear doors
 *   across its floors. Always returns double icons so the UI renders both door
 *   states consistently, even for floors with only one door type.
 */
export function usageToDoorIconFiltered(
  usage: UsageMap,
  hasFrontDoor: boolean,
  hasRearDoor: boolean,
  // Callers pass doorUnknownAsOpening as a bare boolean; the object form is the
  // typed equivalent. Both are read — see the resolution just below.
  config?: DoorIconConfig | boolean,
  bankHasMixedDoors?: boolean,
): string | null {
  const frontSteady = resolveSteadyDoorState(usage, Usage.FrontDoorOpened, Usage.FrontDoorClosed);
  const rearSteady = resolveSteadyDoorState(usage, Usage.RearDoorOpened, Usage.RearDoorClosed);

  // HONOUR doorUnknownAsOpening. This function accepted `config` and never read
  // it, so the setting was wired end to end — store, every call site, the
  // serializer — and then silently discarded at the last step. An operator who
  // enabled it saw no change, and every door cycle raised a warning triangle
  // instead, because the interval between the two limit switches reads as
  // neither open nor closed.
  //
  // That interval is precisely what the setting was added to interpret. Whether
  // it means "moving" or "something is wrong" is a property of the site, not of
  // this module, which is why it is a setting and not an inference.
  const resolved =
    typeof config === "boolean" ? { deriveUnknownAsOpening: config } : (config ?? {});
  const unknownAsOpening = resolved.deriveUnknownAsOpening ?? false;

  const frontTransit = unknownAsOpening && pairCompleteButUnresolved(frontSteady);
  const rearTransit = unknownAsOpening && pairCompleteButUnresolved(rearSteady);

  const frontOpen = hasFrontDoor && frontSteady.open;
  const frontClosed = hasFrontDoor && frontSteady.closed;
  const frontOpening = hasFrontDoor && ((usage[Usage.FrontDoorOpening] ?? false) || frontTransit);
  const frontClosing = hasFrontDoor && (usage[Usage.FrontDoorClosing] ?? false);
  const rearOpen = hasRearDoor && rearSteady.open;
  const rearClosed = hasRearDoor && rearSteady.closed;
  const rearOpening = hasRearDoor && ((usage[Usage.RearDoorOpening] ?? false) || rearTransit);
  const rearClosing = hasRearDoor && (usage[Usage.RearDoorClosing] ?? false);

  const icon = bankHasMixedDoors
    ? resolveDoorIconMixed(
        hasFrontDoor,
        hasRearDoor,
        frontOpen,
        frontClosed,
        frontOpening,
        frontClosing,
        rearOpen,
        rearClosed,
        rearOpening,
        rearClosing,
      )
    : resolveDoorIcon(
        frontOpen,
        frontClosed,
        frontOpening,
        frontClosing,
        rearOpen,
        rearClosed,
        rearOpening,
        rearClosing,
      );
  if (icon) return icon;

  // Ask "did anything report?" over the CONFIGURED doors only.
  //
  // Everything above this line is masked by hasFrontDoor/hasRearDoor, so an
  // unconfigured door can never contribute an icon. Testing presence over the
  // unmasked map broke that symmetry: on a bank with no rear doors, rear-door
  // traffic still counted as "door data", no icon could be resolved from it
  // because the mask had already discarded it, and the fall-through painted a
  // warning triangle on every such car. A door the operator never configured
  // must not be able to raise an alarm about itself.
  if (!usageHasDoorData(usage, { hasFrontDoor, hasRearDoor })) return null;

  // Both signals were observed for the configured door(s) but currently read
  // Open=false/Closed=false with no Opening/Closing message ever received:
  // a genuine "we don't know" anomaly, not something to animate as motion.
  return ElevatorIcon.Warning;
}

/** Resolve direction icon from a typed UsageMap. Returns null when idle. */
export function usageToDirectionIcon(usage: UsageMap): string | null {
  const up = usage[Usage.Up] ?? false;
  const down = usage[Usage.Down] ?? false;

  if (up && !down) return ElevatorIcon.Up;
  if (down && !up) return ElevatorIcon.Down;
  return null;
}

// ---------------------------------------------------------------------------
// Legacy bitmask-based API — delegates to typed functions
// ---------------------------------------------------------------------------
// These are kept for backward compatibility with components that still pass
// bitmask + prefix (ControlPanelStateView, ruleEngine, replay files, tests).
// The prefix is stripped and the Usage enum key is looked up by name.

function bitmaskToUsageMap(bitmask: Record<string, boolean>, prefix: string): UsageMap {
  const usage: UsageMap = {};
  const usagePrefix = `${prefix}Usage:`;
  Object.keys(bitmask).forEach((key) => {
    if (key.startsWith(usagePrefix)) {
      const name = key.substring(usagePrefix.length);
      const code = Usage[name as keyof typeof Usage];
      if (code !== undefined) {
        usage[code] = bitmask[key];
      }
    }
  });
  return usage;
}

/** @deprecated Use usageToIcon(msg.usage) instead */
export function bitmaskToIcon(bitmask: Record<string, boolean>, prefix: string): string | null {
  return usageToIcon(bitmaskToUsageMap(bitmask, prefix));
}

/** @deprecated Use usageToIcon(msg.usage) instead */
export function socketMessageToIcon(msg: SocketMessageInterface): string | null {
  return usageToIcon(msg.usage);
}

/** @deprecated Use usageToDoorIcon(msg.usage, config) instead */
export function bitmaskToDoorIcon(
  bitmask: Record<string, boolean>,
  prefix: string,
  doorUnknownAsOpening?: boolean,
): string | null {
  // Backward compat: pass boolean as config for existing callers.
  const config =
    typeof doorUnknownAsOpening === "boolean"
      ? { deriveUnknownAsOpening: doorUnknownAsOpening }
      : doorUnknownAsOpening;
  return usageToDoorIcon(bitmaskToUsageMap(bitmask, prefix), config);
}

/** @deprecated Use usageToDoorIconFiltered instead */
export function bitmaskToDoorIconFiltered(
  bitmask: Record<string, boolean>,
  prefix: string,
  hasFrontDoor: boolean,
  hasRearDoor: boolean,
  doorUnknownAsOpening?: boolean,
  bankHasMixedDoors?: boolean,
): string | null {
  // Backward compat: pass boolean as config for existing callers.
  const config =
    typeof doorUnknownAsOpening === "boolean"
      ? { deriveUnknownAsOpening: doorUnknownAsOpening }
      : doorUnknownAsOpening;
  return usageToDoorIconFiltered(
    bitmaskToUsageMap(bitmask, prefix),
    hasFrontDoor,
    hasRearDoor,
    config,
    bankHasMixedDoors,
  );
}

/** @deprecated Use usageToDirectionIcon(msg.usage) instead */
export function bitmaskToDirectionIcon(
  bitmask: Record<string, boolean>,
  prefix: string,
): string | null {
  return usageToDirectionIcon(bitmaskToUsageMap(bitmask, prefix));
}
